import { Component, Input, OnInit } from '@angular/core';
import { drawerEnter, drawerLeave } from '@animations/modal-drawer';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AlertController, ModalController } from '@ionic/angular';
import { regexList } from '@shared/constants/regex-list';
import { Group } from '@shared/models/group';
import { GroupFilters } from '@shared/models/group-filters';
import { Lead } from '@shared/models/lead';
import { defaultPaginationState } from '@shared/models/pagination-state';
import { Sorting } from '@shared/models/sorting';
import { GroupsService } from '@shared/services/groups.service';
import { LoadingState } from '@shared/types/loading-state';
import { Permission } from '@shared/types/permission';
import { SessionService } from '@shared/services/session.service';
import { debounceTime, distinctUntilChanged, filter, Observable, switchMap, take } from 'rxjs';
import { CreateLeadCustomerComponent } from '../create-lead-customer/create-lead-customer.component';

@Component({
  selector: 'app-lead-customer-selector',
  templateUrl: './lead-customer-selector.component.html',
  standalone: false,
})
export class LeadCustomerSelectorComponent implements OnInit {
  @Input() lead: Lead;

  groupFilters: GroupFilters;
  groups$: Observable<Group[]>;
  loadingState$: Observable<LoadingState>;
  permissions$: Observable<Permission[] | undefined>;
  searchForm = new FormGroup({
    search: new FormControl('', [Validators.required, Validators.minLength(3)]),
  });
  sorting: Sorting = { key: 'id', direction: 'asc' };
  selectedGroupToConfirm: Group | undefined;
  hasDuplicateFilters = false;
  searchActive = false;

  constructor(
    private groupsService: GroupsService,
    private modalController: ModalController,
    private alertController: AlertController,
    private sessionService: SessionService,
  ) {}

  ngOnInit(): void {
    this.groups$ = this.groupsService.getGroups();
    this.loadingState$ = this.groupsService.getGroupsLoading();
    this.permissions$ = this.sessionService.getSessionPermissions();
    this.groupsService.clearGroups();
    this.initialiseDuplicateFilters();

    if (this.hasDuplicateFilters) {
      this.groupsService.loadGroupDuplicates(defaultPaginationState, this.sorting, this.groupFilters);
      this.loadingState$
        .pipe(
          filter((loadingState) => loadingState === 'loaded'),
          take(1),
          switchMap(() => this.groups$.pipe(take(1))),
          filter((groups) => groups.length === 0),
        )
        .subscribe(() => this.openSearch());
    }

    this.searchForm.controls.search.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((value) => {
      if (this.searchForm.valid) {
        this.selectedGroupToConfirm = undefined;
        let filters: GroupFilters = { active: true, group_type: 'customer' };
        if (regexList.is_email.test(value!)) {
          filters = { ...filters, primary_contact_email: value! };
        } else if (regexList.group_id.test(value!)) {
          filters = { ...filters, id: value! };
        } else {
          filters = { ...filters, name: value! };
        }
        this.groupsService.loadGroups(defaultPaginationState, this.sorting, { ...filters });
      } else {
        this.groupsService.clearGroups();
      }
    });
  }

  private initialiseDuplicateFilters(): void {
    const emailDomain = this.lead.attributes?.contact_email?.includes('@')
      ? this.lead.attributes.contact_email.split('@')[1]
      : undefined;

    this.groupFilters = {
      group_type: 'customer',
      duplicates_for_name: this.lead.attributes?.customer_name || undefined,
      duplicates_for_email_domain: emailDomain,
      duplicates_for_company_registration_number: this.lead.attributes?.company_registration_number || undefined,
      duplicates_for_vat_number: this.lead.attributes?.vat_number || undefined,
    };
    this.hasDuplicateFilters = !!(
      this.groupFilters.duplicates_for_name ||
      this.groupFilters.duplicates_for_email_domain ||
      this.groupFilters.duplicates_for_company_registration_number ||
      this.groupFilters.duplicates_for_vat_number
    );
  }

  closeModal() {
    this.modalController.dismiss({});
  }

  openSearch() {
    this.searchActive = true;
    this.selectedGroupToConfirm = undefined;
    this.groupsService.clearGroups();
  }

  selectGroup(group: Group) {
    this.selectedGroupToConfirm =
      this.selectedGroupToConfirm?.attributes.id === group.attributes.id ? undefined : group;
  }

  confirmSelectedGroup() {
    this.modalController.dismiss({ group: this.selectedGroupToConfirm });
  }

  async createCustomer(): Promise<void> {
    this.groups$.pipe(take(1)).subscribe(async (groups) => {
      if (this.hasDuplicateFilters && !this.searchActive && groups.length > 0) {
        const alert = await this.alertController.create({
          header: 'Possible duplicate customers',
          message: 'Existing customer records match the captured lead details. Create a new customer anyway?',
          buttons: [
            { text: 'Cancel', role: 'cancel' },
            {
              text: 'Create anyway',
              role: 'confirm',
              cssClass: 'btn-primary',
              handler: () => this.presentCreateLeadCustomerModal(),
            },
          ],
        });

        await alert.present();
      } else {
        this.presentCreateLeadCustomerModal();
      }
    });
  }

  async presentCreateLeadCustomerModal(): Promise<void> {
    this.modalController.dismiss({});

    const modal = await this.modalController.create({
      component: CreateLeadCustomerComponent,
      id: 'create-lead-customer-modal',
      componentProps: {
        lead: this.lead,
      },
      cssClass: 'drawer-style-sm layered-modal',
      enterAnimation: drawerEnter,
      leaveAnimation: drawerLeave,
    });
    await modal.present();
  }
}
